/*
  Projects map view (03 #10, wave 3) — pure helpers (no React), shared by the map, its list, the city
  fallback, the "Add missing pins" panel and tests.

  Pins: the server stores latitude / longitude / city / emirate on every project (backend/project_geo.py,
  read from the Waze link); a response without those fields (an older server) falls back to reading the
  Waze link here (pinLink.js, the server's rules: a directions link gives its destination, never its start).
  Places: the systems of one site (same name + client, groupBySite) share one row and one pin. Clusters: pins
  closer than a few pixels at the current zoom become one numbered bubble.
*/
import { inServiceArea, LINK_MAX_LENGTH, pinFromLink, routeStartOnly, toPin } from './pinLink';
import {
  canonicalStatus,
  cleanText,
  groupBySite,
  matchesStatusTab,
  projectCity,
  siteKeyOf,
} from './projectsModel';

/** The Mapbox key the map needs (build-time env); '' = the map is not set up (the city list is shown). */
export const mapboxToken = () => cleanText(process.env.REACT_APP_MAPBOX_ACCESS_TOKEN);

export const NO_CITY = '';
export const NO_CITY_LABEL = 'City not known';

const finite = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));

/** { lat, lng } of a project's map pin, or null. */
export function pinOf(project) {
  if (!project) return null;
  if (Object.prototype.hasOwnProperty.call(project, 'latitude')) {
    const lat = finite(project.latitude);
    const lng = finite(project.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && (lat || lng)) {
      return { lat, lng };
    }
    return null;
  }
  return pinFromLink(project.waze_url);
}

const URL_PATTERN = /^https?:\/\//i;
const PLAIN_COORDINATES = /^\s*(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

const roundTo = (value, places = 7) => Number(value.toFixed(places));
const isWazeHost = (link) => {
  try {
    const host = new URL(link).hostname.toLowerCase();
    return host === 'waze.com' || host.endsWith('.waze.com');
  } catch {
    return false;
  }
};

/** A Waze link that navigates to the pin (same as the server's project_geo.waze_link_for). */
export const wazeLinkFor = ({ lat, lng }) => `https://waze.com/ul?ll=${roundTo(lat)},${roundTo(lng)}&navigate=yes`;

// The server's messages (project_geo.read_pin_input), word for word.
export const PIN_MESSAGES = {
  noPin: 'No map pin in this link — in Waze open the place, tap Share › Copy link, or paste coordinates',
  routeStart: 'No map pin in this link — it only has the starting point of a route. In Waze open the place itself, tap Share › Copy link, or paste coordinates',
  outside: 'This pin is far from the UAE and Saudi Arabia — check the link or the coordinates',
};

/** null when the pin is in the Gulf; else why not — { message, swapped } when the numbers only fit the other way round. */
function areaProblem(pin) {
  if (inServiceArea(pin)) return null;
  const swapped = { lat: pin.lng, lng: pin.lat };
  if (inServiceArea(swapped)) return { message: `These look swapped — latitude first: ${swapped.lat}, ${swapped.lng}`, swapped };
  return { message: PIN_MESSAGES.outside };
}

/**
 * What the "Add missing pins" field reads — the same rules as the server (project_geo.read_pin_input):
 * { ok: true, pin, link, kind: 'waze' | 'coordinates' | 'map-link' } or { ok: false, message, swapped? };
 * null when empty. `swapped` ({ lat, lng }) = the numbers the other way round, offered as a one-click fix.
 */
export function parsePinInput(text) {
  const raw = cleanText(text);
  if (!raw) return null;
  if (raw.length > LINK_MAX_LENGTH) return { ok: false, message: `The link is too long (at most ${LINK_MAX_LENGTH} characters)` };
  const plain = raw.match(PLAIN_COORDINATES);
  let pin;
  let kind;
  if (plain) {
    pin = toPin(plain[1], plain[2]);
    if (!pin) return { ok: false, message: 'These are not coordinates — use latitude, longitude like 24.4539, 54.3773' };
    kind = 'coordinates';
  } else {
    if (!URL_PATTERN.test(raw)) {
      return { ok: false, message: 'Paste the whole link (starting with https://) or coordinates like 24.4539, 54.3773' };
    }
    pin = pinFromLink(raw);
    if (!pin) return { ok: false, message: routeStartOnly(raw) ? PIN_MESSAGES.routeStart : PIN_MESSAGES.noPin };
    kind = isWazeHost(raw) ? 'waze' : 'map-link';
  }
  const problem = areaProblem(pin);
  if (problem) return { ok: false, ...problem };
  return { ok: true, pin, link: kind === 'waze' ? raw : wazeLinkFor(pin), kind };
}

/** "24.4539, 54.3773" */
export const formatPin = (pin) => (pin ? `${pin.lat.toFixed(4)}, ${pin.lng.toFixed(4)}` : '');

/** Coordinates typed into the Location field ("25.1041263, 55.2258330" or a map link) — offered as a pin. */
export function locationPin(project) {
  const parsed = parsePinInput(project?.location);
  return parsed?.ok ? parsed.pin : null;
}

/* ---------- status colours of the pins ---------- */

/** Pin / dot tone by project status: Active green, On hold amber, Completed slate, anything else grey. */
export function pinTone(status) {
  if (matchesStatusTab({ status }, 'Active')) return 'green';
  if (matchesStatusTab({ status }, 'On Hold')) return 'amber';
  if (matchesStatusTab({ status }, 'Completed')) return 'slate';
  return 'gray';
}
export const PIN_TONE_COLOR = {
  green: 'var(--status-green)',
  amber: 'var(--status-amber)',
  slate: 'var(--navy-300)',
  gray: 'var(--status-gray)',
};
export const PIN_LEGEND = [
  { tone: 'green', label: 'Active' },
  { tone: 'amber', label: 'On hold' },
  { tone: 'slate', label: 'Completed' },
];

const TONE_RANK = { green: 0, amber: 1, slate: 2, gray: 3 };

/* ---------- places: one row / pin per site ---------- */

/**
 * The sites of these projects, by name: [{ key, name, client, projects, pin, pinned, missing, city, tone,
 * systems, href }]. A site's pin is the first pinned project's (the systems of one site share the place);
 * `missing` = its projects without a pin; `tone` = the most "alive" status (Active before On hold …).
 */
export function buildPlaces(projects) {
  const entries = groupBySite(projects || []);
  return entries.map((entry) => {
    const list = entry.kind === 'group' ? entry.projects : [entry.project];
    const first = list[0];
    const pinnedProjects = list.filter((project) => pinOf(project));
    const pin = pinnedProjects.length ? pinOf(pinnedProjects[0]) : null;
    const tone = list.map((project) => pinTone(project.status)).sort((a, b) => TONE_RANK[a] - TONE_RANK[b])[0] || 'gray';
    return {
      key: entry.kind === 'group' ? entry.key : `project:${first.id}`,
      name: entry.kind === 'group' ? entry.name : cleanText(first.name) || 'Untitled project',
      client: entry.kind === 'group' ? entry.client : cleanText(first.client),
      projects: list,
      pin,
      pinned: pinnedProjects.length,
      missing: list.filter((project) => !pinOf(project)),
      city: list.map((project) => projectCity(project)).find(Boolean) || NO_CITY,
      tone,
      systems: list.map((project) => cleanText(project.system_type)),
    };
  }).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.key.localeCompare(b.key));
}

/** Places by city, the city with most projects first; places without a city last. */
export function groupPlacesByCity(places) {
  const byCity = new Map();
  (places || []).forEach((place) => {
    if (!byCity.has(place.city)) byCity.set(place.city, []);
    byCity.get(place.city).push(place);
  });
  return [...byCity.entries()]
    .map(([city, list]) => ({
      city,
      label: city || NO_CITY_LABEL,
      places: list,
      projects: list.reduce((sum, place) => sum + place.projects.length, 0),
      pinned: list.reduce((sum, place) => sum + place.pinned, 0),
    }))
    .sort((a, b) => {
      if (!a.city || !b.city) return a.city ? -1 : (b.city ? 1 : 0);
      return b.projects - a.projects || a.city.localeCompare(b.city);
    });
}

/** { total, pinned, missing } projects. */
export function pinCoverage(projects) {
  const total = (projects || []).length;
  const pinned = (projects || []).filter((project) => pinOf(project)).length;
  return { total, pinned, missing: total - pinned };
}

/**
 * Another project of the same site (same site name: 'Nobu Hotel — HA KNX' is a system of 'Nobu Hotel') that has a
 * pin — offered as "Use the pin of …".
 */
export function sameSitePinSource(project, projects) {
  const key = siteKeyOf(project);
  if (!key) return null;
  return (projects || []).find((other) => other.id !== project.id && siteKeyOf(other) === key && pinOf(other)) || null;
}

/** "Nobu Hotel · BMS" */
export const projectLabel = (project) => [cleanText(project?.name) || 'Untitled project', cleanText(project?.system_type)].filter(Boolean).join(' · ');

/** "On hold" for 'On Hold' etc. */
export const statusLabel = (status) => {
  const value = canonicalStatus(status);
  return value === 'On Hold' ? 'On hold' : value || 'No status';
};

/* ---------- clusters ---------- */

/**
 * Greedy clustering on screen positions: each item { key, x, y, … } joins the first cluster whose centre is
 * within `radius` px, else starts one. Returns [{ key, x, y, items }] — the centre is the mean position.
 * Places at the same spot (the systems of one site are already one place) always end up together.
 */
export function clusterByDistance(items, radius = 34) {
  const clusters = [];
  (items || []).forEach((item) => {
    const near = clusters.find((cluster) => Math.hypot(cluster.x - item.x, cluster.y - item.y) <= radius);
    if (near) {
      near.items.push(item);
      near.x = near.items.reduce((sum, entry) => sum + entry.x, 0) / near.items.length;
      near.y = near.items.reduce((sum, entry) => sum + entry.y, 0) / near.items.length;
    } else {
      clusters.push({ x: item.x, y: item.y, items: [item] });
    }
  });
  return clusters.map((cluster) => ({
    ...cluster,
    key: cluster.items.length === 1 ? cluster.items[0].key : `cluster:${cluster.items.map((item) => item.key).sort().join('|')}`,
  }));
}

/** [[west, south], [east, north]] around the pins (null for none). */
export function boundsOf(pins) {
  const list = (pins || []).filter(Boolean);
  if (!list.length) return null;
  const lats = list.map((pin) => pin.lat);
  const lngs = list.map((pin) => pin.lng);
  return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
}

/**
 * Which markers get their name written next to them: greedy, in order (clusters of one site first, then pins
 * top to bottom); a label is skipped when it would cover another marker or label, or leave the map.
 * markers: [{ key, x, y, kind: 'pin' | 'cluster', text }] (screen px; a pin's x / y is its tip).
 */
export function placeLabels(markers, { width, height, charWidth = 6.6, labelHeight = 16 } = {}) {
  const list = (markers || []).filter(Boolean);
  const boxOf = (marker) => (marker.kind === 'cluster'
    ? { key: marker.key, l: marker.x - 18, r: marker.x + 18, t: marker.y - 18, b: marker.y + 18 }
    : { key: marker.key, l: marker.x - 14, r: marker.x + 14, t: marker.y - 32, b: marker.y });
  const obstacles = list.map(boxOf);
  const placed = [];
  const labelled = new Set();
  const overlaps = (a, b) => !(a.r <= b.l || a.l >= b.r || a.b <= b.t || a.t >= b.b);
  [...list]
    .filter((marker) => cleanText(marker.text))
    .sort((a, b) => (a.kind === b.kind ? a.y - b.y : (a.kind === 'cluster' ? -1 : 1)))
    .forEach((marker) => {
      const centreY = marker.kind === 'cluster' ? marker.y : marker.y - 16;
      const left = marker.x + (marker.kind === 'cluster' ? 21 : 15);
      const box = { l: left, r: left + cleanText(marker.text).length * charWidth + 6, t: centreY - labelHeight / 2, b: centreY + labelHeight / 2 };
      if (box.l < 0 || box.r > width - 4 || box.t < 0 || box.b > height) return;
      if (obstacles.some((other) => other.key !== marker.key && overlaps(box, other))) return;
      if (placed.some((other) => overlaps(box, other))) return;
      placed.push(box);
      labelled.add(marker.key);
    });
  return labelled;
}
